import type { LifecycleAnalysisItem, LegislativeStatus } from '../types/lifecycle';
import { trackingLevelLabel } from '../utils/lifecycleFormat';
import { RiderBadge } from './RiderBadge';
import { SequenceBadge } from './SequenceSurfacing';
import { InstitutionalWarningBadge } from './InstitutionalWarning';
import {
  LegislativeStageBadge,
  NextStepLine,
} from './LegislativePath';

interface LifecycleAnalysisCardProps {
  item: LifecycleAnalysisItem;
  selected: boolean;
  onClick: () => void;
}

function riskLevelForBand(item: LifecycleAnalysisItem): string {
  return item.final_risk_level ?? item.risk_level ?? item.tracking_level ?? 'unknown';
}

function riskScoreForBand(item: LifecycleAnalysisItem): number | null {
  return item.risk_score ?? item.tracking_score ?? null;
}

function snippet(item: LifecycleAnalysisItem): string {
  const text = item.plain_explanation || item.summary || '';
  return text.length > 170 ? `${text.slice(0, 170)}…` : text;
}

function CommitteeBadges({ status }: { status: LegislativeStatus | null | undefined }) {
  const committees = (status?.committees ?? []).filter((committee) => committee.role !== 'organizacni');
  const lead = committees.find((committee) => committee.role === 'garancni') ?? committees[0] ?? null;
  if (!lead) {
    return <span className="badge badge--committee-missing">Výbor zatím nepublikován</span>;
  }
  const remaining = committees.filter((committee) => committee.code !== lead.code).length;
  return (
    <>
      <span className="badge badge--lead" title={lead.role === 'garancni' ? 'Garanční výbor' : 'Věcný výbor'}>
        <span aria-hidden="true">⚖</span> {lead.name}
      </span>
      {remaining > 0 && (
        <span className="badge badge--committee-more">+{remaining} další {remaining === 1 ? 'výbor' : 'výbory'}</span>
      )}
    </>
  );
}

function AnalysisBadges({ item }: { item: LifecycleAnalysisItem }) {
  const isAlert = item.alert === 'yes';
  return (
    <>
      {isAlert && <span className="badge badge--alert" aria-label="Alert">⚠ Alert</span>}
      {item.possible_rider?.label && <RiderBadge label={item.possible_rider.label} />}
      {item.sequence_surfacing && <SequenceBadge surfacing={item.sequence_surfacing} />}
      <InstitutionalWarningBadge context={item.institutional_context} />
      {item.constitutional_relevance === 'high' && (
        <span className="badge badge--constitutional">ústavní dopad</span>
      )}
      {item.confidence != null && (
        <span className="badge badge--conf">Spolehlivost: {Math.round(item.confidence * 100)} %</span>
      )}
    </>
  );
}

export function LifecycleAnalysisCard({ item, selected, onClick }: LifecycleAnalysisCardProps) {
  const legislative = item.legislative_status;
  const riskLevel = riskLevelForBand(item);
  const riskScore = riskScoreForBand(item);
  const riskLabel = trackingLevelLabel(riskLevel).toLocaleLowerCase('cs-CZ');
  const text = snippet(item);
  const hasStageLabel = Boolean(legislative?.stage_label);

  return (
    <div className={`tracked-card tracked-card--analysis tracked-card--risk-${riskLevel} ${selected ? 'tracked-card--selected' : ''}`} role="listitem">
      <button type="button" className="tracked-card__btn" onClick={onClick} aria-pressed={selected}>
        <div className="tracked-card__riskband" aria-label={`Riziko ${riskScore ?? 'neznámé'}, ${riskLabel}`}>
          <span className="tracked-card__risk-label">Riziko</span>
          <span className="tracked-card__risk-score">{riskScore ?? '–'}</span>
          <span className="tracked-card__risk-level">{riskLabel}</span>
        </div>

        <div className="tracked-card__content">
          <div className="tracked-card__top">
            <div className="tracked-card__main">
              <div className="tracked-card__title">{item.title}</div>
              <div className="tracked-card__badges">
                {hasStageLabel ? (
                  <LegislativeStageBadge status={legislative} fallbackLabel={null} />
                ) : (
                  <span className="badge badge--stage badge--stage-unknown">Fáze neurčena</span>
                )}
                <CommitteeBadges status={legislative} />
                <AnalysisBadges item={item} />
              </div>
            </div>
            <span className="tracked-card__chev" aria-hidden="true">›</span>
          </div>

          {text && <p className="tracked-card__summary tracked-card__summary--analysis">{text}</p>}
          <NextStepLine nextStep={legislative?.next_step} />
        </div>
      </button>
    </div>
  );
}
